import { Cloud, CloudOff, Loader2, AlertCircle } from 'lucide-react';
import { useConnectivity } from './useConnectivity';
import { useSyncStatus } from './useSyncStatus';
import { cn } from '@/lib/utils';

interface SyncStatusBadgeProps {
  /**
   * `full` spells the state out. `compact` is the phone header's light.
   * `bar` is the desktop top row's (compact chrome, 2026-10-10; Michael: "it
   * uses too much space"): the icon alone while all is well, a word or two
   * otherwise.
   */
  variant?: 'full' | 'compact' | 'bar';
  /** The older spelling of variant="compact"; still honoured. */
  compact?: boolean;
}

export function SyncStatusBadge({ variant, compact = false }: SyncStatusBadgeProps) {
  const { isOnline, connectionQuality } = useConnectivity();
  const { isSyncing, pendingCount, syncError } = useSyncStatus();
  const kind = variant ?? (compact ? 'compact' : 'full');

  // Determine status
  let status: 'online' | 'syncing' | 'pending' | 'offline' | 'error';
  let label: string;
  let Icon: typeof Cloud;

  if (!isOnline) {
    status = 'offline';
    label = 'Offline';
    Icon = CloudOff;
  } else if (syncError) {
    status = 'error';
    label = 'Sync Error';
    Icon = AlertCircle;
  } else if (isSyncing) {
    status = 'syncing';
    label = 'Syncing...';
    Icon = Loader2;
  } else if (pendingCount > 0) {
    status = 'pending';
    label = `${pendingCount} pending`;
    Icon = Cloud;
  } else {
    status = 'online';
    label = connectionQuality === 'slow' ? 'Online (slow)' : 'Online';
    Icon = Cloud;
  }

  const colorClasses = {
    online: 'text-signal-500',
    // The row's words are 12px: steel-500 and danger-500 over their tints
    // were 3.27:1 there, one step lighter is 4.72:1 and 6.49:1 (compact
    // chrome review 4, 2026-10-11). The phone light and the pill keep theirs.
    syncing: kind === 'bar' ? 'text-steel-400' : 'text-steel-500',
    pending: 'text-amber-500',
    // The light is the only visible offline signal, so it must be read:
    // graphite-500 on the grey pill was 1.70:1, graphite-200 is 6.00:1.
    offline: 'text-graphite-200',
    error: kind === 'bar' ? 'text-danger-300' : 'text-danger-500',
  };

  const bgClasses = {
    online: 'bg-signal-500/10',
    syncing: 'bg-steel-500/10',
    pending: 'bg-amber-500/10',
    offline: 'bg-graphite-700',
    error: 'bg-danger-500/10',
  };

  if (kind === 'full') {
    return (
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm',
          bgClasses[status],
          colorClasses[status]
        )}
      >
        <Icon className={cn('w-4 h-4', isSyncing && 'animate-spin')} />
        <span>{label}</span>
      </div>
    );
  }

  // What the light shows. The desk row says nothing while all is well (the
  // full pill was 89px on every route) and a word or two otherwise; the phone
  // shows "Offline" or the count, as it always has.
  const shown = kind === 'bar'
    ? { online: connectionQuality === 'slow' ? 'Slow' : null, syncing: 'Syncing', pending: label, offline: 'Offline', error: 'Sync error' }[status]
    : !isOnline ? 'Offline' : pendingCount > 0 ? String(pendingCount) : null;
  // Where that is not the whole state, a screen reader hears the label
  // instead — once, so the short text is hidden from it.
  const short = shown !== label;

  return (
    <div
      className={cn(
        kind === 'bar'
          ? 'flex h-7 items-center gap-1.5 rounded-full px-2 text-xs'
          : 'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs',
        bgClasses[status],
        colorClasses[status]
      )}
      title={label}
    >
      <Icon className={cn(kind === 'bar' ? 'h-4 w-4' : 'w-3.5 h-3.5', isSyncing && 'animate-spin')} aria-hidden="true" />
      {shown && <span aria-hidden={short || undefined}>{shown}</span>}
      {short && <span className="sr-only">{label}</span>}
    </div>
  );
}
